import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type {
  PlanungAbsence,
  PlanungBoard,
  PlanungRow,
} from "@/lib/montageplanung";
import { AbsenceBar } from "@/pages/montageplanung/AbsenceBar";
import { AssignmentBar } from "@/pages/montageplanung/AssignmentBar";
import { computeRowTrackLayout } from "@/pages/montageplanung/assignmentLayout";
import { absenceLayoutKey } from "@/pages/montageplanung/vacationSicknessOverlay";
import {
  addDays,
  enumerateDays,
  groupConsecutive,
  isoWeek,
  isNonWorkDay,
  isSameDay,
  isWeekend,
  MONTH_NAMES,
  nonWorkDayTitle,
  toIso,
} from "@/pages/montageplanung/dateUtils";
import { rowLabelFull } from "@/pages/montageplanung/displayLabels";
import { MerosStateBars } from "@/pages/montageplanung/MerosPlanRow";
import { PlanningHeadRow } from "@/pages/montageplanung/PlanningHeadRow";
import { PlanningLegend } from "@/pages/montageplanung/PlanningLegend";
import {
  MP_DISPLAY_HEAD_H,
  MP_DISPLAY_LEGEND_H,
  MP_DISPLAY_SEPARATOR_H,
  MP_DISPLAY_TODAY_OFFSET,
} from "@/pages/montageplanung/layoutConstants";
import {
  displayUiScaleFactor,
  normalizeDisplayUiScale,
  scaleDisplayPx,
} from "@/pages/montageplan/displayUiScale";
import {
  findUnavailabilityOnDay,
  isUnavailableDay,
  unavailabilityDayTitle,
} from "@/pages/montageplanung/unavailabilityUtils";
import { UnavailabilityReasonLabels } from "@/pages/montageplanung/UnavailabilityReasonLabels";
import { useScrollPan } from "@/pages/montageplanung/useScrollPan";
import "@/pages/montageplanung/montageplanung.css";
import "./montageplanDisplay.css";

function rowAbsences(board: PlanungBoard | null, row: PlanungRow): PlanungAbsence[] {
  if (!board || row.kind !== "employee" || !row.employee_login) return [];
  const login = row.employee_login.toLowerCase();
  return (board.absences ?? []).filter((a) => a.user_login.toLowerCase() === login);
}

export interface DisplayLayoutMetrics {
  labelW: number;
  dayW: number;
  barH: number;
  barGap: number;
  rowPadTop: number;
  rowPadBottom: number;
  visibleDays: number;
}

const DISPLAY_MIN_BAR_H = 9;
const DISPLAY_MAX_BAR_H = 32;

function stackHeightForLanes(laneCount: number, barH: number, barGap: number): number {
  const lanes = Math.max(1, laneCount);
  return lanes * barH + Math.max(0, lanes - 1) * barGap;
}

function barTopForTrack(
  lane: number,
  laneCount: number,
  trackHeight: number,
  barH: number,
  barGap: number,
): number {
  const stackH = stackHeightForLanes(laneCount, barH, barGap);
  const offset = Math.max(0, Math.floor((trackHeight - stackH) / 2));
  return offset + lane * (barH + barGap);
}

function distributeHeightSlack(
  rowHeights: Map<number, number>,
  separatorHeights: number[],
  slack: number,
  laneCountByRow: Map<number, number>,
): void {
  if (slack <= 0) return;

  const weights: { kind: "row" | "sep"; id: number; weight: number }[] = [];
  for (const [rowId, lanes] of laneCountByRow) {
    weights.push({ kind: "row", id: rowId, weight: Math.max(1, lanes) });
  }
  for (let i = 0; i < separatorHeights.length; i++) {
    weights.push({ kind: "sep", id: i, weight: 0.35 });
  }
  if (weights.length === 0) return;

  const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0);
  let remaining = slack;
  weights.forEach((item, index) => {
    const extra =
      index === weights.length - 1
        ? remaining
        : Math.floor(slack * (item.weight / totalWeight));
    remaining -= extra;
    if (item.kind === "row") {
      rowHeights.set(item.id, (rowHeights.get(item.id) ?? 0) + extra);
    } else {
      separatorHeights[item.id] = (separatorHeights[item.id] ?? MP_DISPLAY_SEPARATOR_H) + extra;
    }
  });
}

function rowHeightForLanes(
  laneCount: number,
  barH: number,
  barGap: number,
  pad: number,
): number {
  const lanes = Math.max(1, laneCount);
  return pad * 2 + lanes * barH + Math.max(0, lanes - 1) * barGap;
}

function computeSquareDayWidth(
  trackW: number,
  rowHeights: Map<number, number>,
  separatorHeights: number[],
  uiScale: number,
): { dayW: number; visibleDays: number } {
  const sampleHeights = [...rowHeights.values(), ...separatorHeights];
  const avgH =
    sampleHeights.length > 0
      ? sampleHeights.reduce((sum, h) => sum + h, 0) / sampleHeights.length
      : scaleDisplayPx(36, uiScale);
  const minDayW = scaleDisplayPx(26, uiScale);
  const maxDayW = scaleDisplayPx(54, uiScale);
  const targetDayW = Math.round(Math.max(minDayW, Math.min(maxDayW, avgH * 0.92)));
  const visibleDays = Math.max(8, Math.floor(trackW / targetDayW));
  const dayW = Math.max(minDayW, Math.min(targetDayW, Math.floor(trackW / visibleDays)));
  return { dayW, visibleDays };
}

function computeDisplayMetrics(
  laneCountByRow: Map<number, number>,
  separatorCount: number,
  viewportW: number,
  gridHeight: number,
  uiScale: number,
): {
  metrics: DisplayLayoutMetrics;
  rowHeights: Map<number, number>;
  separatorHeights: number[];
} {
  const minBarH = scaleDisplayPx(DISPLAY_MIN_BAR_H, uiScale);
  const maxBarH = scaleDisplayPx(DISPLAY_MAX_BAR_H, uiScale);
  const labelW = scaleDisplayPx(
    Math.min(168, Math.max(96, Math.round(viewportW * 0.11))),
    uiScale,
  );
  const trackW = Math.max(scaleDisplayPx(320, uiScale), viewportW - labelW);
  const separatorHeights = Array.from({ length: separatorCount }, () =>
    scaleDisplayPx(MP_DISPLAY_SEPARATOR_H, uiScale),
  );
  const sepTotal = () => separatorHeights.reduce((sum, h) => sum + h, 0);
  const available = Math.max(120, gridHeight);

  const laneCounts = [...laneCountByRow.values()];
  const dataRowCount = Math.max(1, laneCounts.length);
  const sumLanes = laneCounts.reduce((sum, lanes) => sum + Math.max(1, lanes), 0) || dataRowCount;
  const sumInnerGaps = laneCounts.reduce(
    (sum, lanes) => sum + Math.max(0, Math.max(1, lanes) - 1),
    0,
  );

  let pad = scaleDisplayPx(3, uiScale);
  let barGap = scaleDisplayPx(2, uiScale);
  const dataAvailable = () => Math.max(80, available - sepTotal());
  let barH = Math.floor(
    (dataAvailable() - dataRowCount * pad * 2 - sumInnerGaps * barGap) / sumLanes,
  );

  const fitDataHeights = (height: number, gap: number, barHeight: number) => {
    if (laneCounts.length === 0) {
      return dataRowCount * Math.max(28, height * 2 + barHeight);
    }
    let total = 0;
    for (const lanes of laneCounts) {
      total += rowHeightForLanes(lanes, barHeight, gap, height);
    }
    return total;
  };

  const fitTotal = (height: number, gap: number, barHeight: number) =>
    sepTotal() + fitDataHeights(height, gap, barHeight);

  let guard = 0;
  while (
    fitTotal(pad, barGap, barH) > available &&
    (pad > scaleDisplayPx(1, uiScale) || barGap > scaleDisplayPx(1, uiScale) || barH > minBarH) &&
    guard < 64
  ) {
    guard += 1;
    if (pad > scaleDisplayPx(1, uiScale)) pad -= 1;
    else if (barGap > scaleDisplayPx(1, uiScale)) barGap -= 1;
    else barH -= 1;
    barH = Math.max(
      minBarH,
      Math.floor(
        (dataAvailable() - dataRowCount * pad * 2 - sumInnerGaps * barGap) / sumLanes,
      ),
    );
  }

  barH = Math.max(minBarH, barH);

  let rowHeights = new Map<number, number>();
  const assignRowHeights = () => {
    rowHeights = new Map<number, number>();
    if (laneCounts.length === 0) {
      const fallback = Math.max(scaleDisplayPx(28, uiScale), Math.floor(dataAvailable() / dataRowCount));
      for (const rowId of laneCountByRow.keys()) {
        rowHeights.set(rowId, fallback);
      }
      return;
    }
    for (const [rowId, lanes] of laneCountByRow) {
      rowHeights.set(rowId, rowHeightForLanes(lanes, barH, barGap, pad));
    }
  };
  assignRowHeights();

  let total = fitTotal(pad, barGap, barH);
  if (total > available && total > 0) {
    const factor = available / total;
    barH = Math.max(minBarH, Math.floor(barH * factor));
    barGap = Math.max(scaleDisplayPx(1, uiScale), Math.floor(barGap * factor));
    pad = Math.max(scaleDisplayPx(1, uiScale), Math.floor(pad * factor));
    assignRowHeights();
    total = fitTotal(pad, barGap, barH);
  }

  let slack = available - total;
  if (slack > 0 && barH < maxBarH) {
    const barIncrease = Math.min(maxBarH - barH, Math.floor(slack / sumLanes));
    if (barIncrease > 0) {
      barH += barIncrease;
      assignRowHeights();
      total = fitTotal(pad, barGap, barH);
      slack = available - total;
    }
  }

  if (slack > 0) {
    distributeHeightSlack(rowHeights, separatorHeights, slack, laneCountByRow);
  }

  const { dayW, visibleDays } = computeSquareDayWidth(trackW, rowHeights, separatorHeights, uiScale);

  return {
    metrics: {
      labelW,
      dayW,
      barH,
      barGap,
      rowPadTop: pad,
      rowPadBottom: pad,
      visibleDays,
    },
    rowHeights,
    separatorHeights,
  };
}

export function MontageplanDisplayBoard({
  board,
  rangeFrom,
  rangeTo,
  today,
  uiScale = 100,
}: {
  board: PlanungBoard;
  rangeFrom: Date;
  rangeTo: Date;
  today: Date;
  uiScale?: number;
  onExtendLeft?: () => void;
  onExtendRight?: () => void;
}) {
  const scaledUi = normalizeDisplayUiScale(uiScale);
  const uiScaleFactor = displayUiScaleFactor(scaledUi);
  const rows = board.rows ?? [];
  const unavailabilities = board.unavailabilities ?? [];
  const days = useMemo(() => enumerateDays(rangeFrom, rangeTo), [rangeFrom, rangeTo]);
  const lastIdx = days.length - 1;
  const todayIdx = useMemo(() => days.findIndex((d) => isSameDay(d, today)), [days, today]);

  const separatorCount = rows.filter((r) => r.kind === "separator").length;

  const assignmentsByRow = useMemo(() => {
    const map = new Map<number, typeof board.assignments>();
    for (const a of board.assignments ?? []) {
      const arr = map.get(a.row_id) ?? [];
      arr.push(a);
      map.set(a.row_id, arr);
    }
    return map;
  }, [board.assignments]);

  const laneCountByRow = useMemo(() => {
    const map = new Map<number, number>();
    const probeMetrics = {
      labelW: 96,
      dayW: 52,
      barH: 20,
      barGap: 2,
      rowPadTop: 2,
      rowPadBottom: 2,
      minRowH: 28,
      hoursBand: 0,
    };
    for (const row of rows) {
      if (row.kind === "separator") continue;
      const layout = computeRowTrackLayout(
        assignmentsByRow.get(row.id) ?? [],
        rowAbsences(board, row),
        rangeFrom,
        lastIdx,
        probeMetrics,
        { reserveHoursBand: false },
      );
      map.set(row.id, layout.laneCount);
    }
    return map;
  }, [rows, board, rangeFrom, lastIdx, assignmentsByRow]);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 720,
  }));
  const headRef = useRef<HTMLDivElement | null>(null);
  const [gridHeight, setGridHeight] = useState(() =>
    Math.max(120, (typeof window !== "undefined" ? window.innerHeight : 720) - MP_DISPLAY_HEAD_H - MP_DISPLAY_LEGEND_H),
  );

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useLayoutEffect(() => {
    const head = headRef.current;
    const root = head?.closest<HTMLElement>(".mp-display-root");
    if (!head || !root) return;

    const updateGridHeight = () => {
      setGridHeight(Math.max(120, root.clientHeight - head.offsetHeight));
    };

    updateGridHeight();
    const observer = new ResizeObserver(updateGridHeight);
    observer.observe(head);
    observer.observe(root);
    return () => observer.disconnect();
  }, [rows.length, separatorCount]);

  const { metrics, rowHeights, separatorHeights } = useMemo(
    () => computeDisplayMetrics(laneCountByRow, separatorCount, viewport.w, gridHeight, scaledUi),
    [laneCountByRow, separatorCount, viewport.w, gridHeight, scaledUi],
  );

  const { labelW, dayW } = metrics;
  const totalWidth = days.length * dayW;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const headTrackScrollRef = useRef<HTMLDivElement | null>(null);
  const centeredRef = useRef(false);
  const [headScrollLeft, setHeadScrollLeft] = useState(0);
  const [headTrackVisibleWidth, setHeadTrackVisibleWidth] = useState(0);

  const headSyncRef = useRef({ left: -1, width: -1 });

  const syncHeadScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const inner = headTrackScrollRef.current;
    if (inner) inner.style.transform = `translateX(${-el.scrollLeft}px)`;
    const nextLeft = el.scrollLeft;
    const nextWidth = Math.max(0, el.clientWidth - labelW);
    const prev = headSyncRef.current;
    if (Math.round(prev.left) === Math.round(nextLeft) && Math.round(prev.width) === Math.round(nextWidth)) {
      return;
    }
    headSyncRef.current = { left: nextLeft, width: nextWidth };
    setHeadScrollLeft(nextLeft);
    setHeadTrackVisibleWidth(nextWidth);
  }, [labelW]);

  const calendarByDay = useMemo(() => {
    const map = new Map<string, (typeof board.calendar_days)[number]>();
    for (const day of board.calendar_days ?? []) {
      map.set(String(day.work_date).slice(0, 10), day);
    }
    return map;
  }, [board.calendar_days]);

  const months = useMemo(
    () =>
      groupConsecutive(
        days,
        (d) => `${d.getFullYear()}-${d.getMonth()}`,
        (d) => `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      ),
    [days],
  );

  const weekGroups = useMemo(
    () =>
      groupConsecutive(
        days,
        (d) => `${d.getFullYear()}-${isoWeek(d)}`,
        (d) => `KW ${isoWeek(d)}`,
      ),
    [days],
  );

  const layoutMetrics = useMemo(
    () => ({
      labelW,
      dayW,
      barH: metrics.barH,
      barGap: metrics.barGap,
      rowPadTop: metrics.rowPadTop,
      rowPadBottom: metrics.rowPadBottom,
      minRowH: DISPLAY_MIN_BAR_H,
      hoursBand: 0,
    }),
    [labelW, dayW, metrics],
  );

  const rowLayouts = useMemo(() => {
    const map = new Map<number, ReturnType<typeof computeRowTrackLayout>>();
    for (const row of rows) {
      if (row.kind === "separator") continue;
      map.set(
        row.id,
        computeRowTrackLayout(
          assignmentsByRow.get(row.id) ?? [],
          rowAbsences(board, row),
          rangeFrom,
          lastIdx,
          layoutMetrics,
          { reserveHoursBand: false },
        ),
      );
    }
    return map;
  }, [rows, assignmentsByRow, board, rangeFrom, lastIdx, layoutMetrics]);

  const scrollToToday = useCallback(() => {
    const el = scrollRef.current;
    if (!el || todayIdx < 0) return;
    el.scrollLeft = Math.max(0, (todayIdx - MP_DISPLAY_TODAY_OFFSET) * dayW);
    syncHeadScroll();
  }, [todayIdx, dayW, syncHeadScroll]);

  useLayoutEffect(() => {
    scrollToToday();
    centeredRef.current = true;
  }, [scrollToToday, rangeFrom, rangeTo, todayIdx]);

  useLayoutEffect(() => {
    centeredRef.current = false;
  }, [today]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncHeadScroll);
    };
    syncHeadScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [syncHeadScroll]);

  const { onPanPointerDown } = useScrollPan({ scrollRef, enabled: true });

  let separatorIndex = 0;

  return (
    <div
      className="mp-display-root"
      style={{ ["--mp-display-scale" as string]: String(uiScaleFactor) }}
    >
      <div ref={headRef} className="mp-display-head">
        <div className="mp-line mp-legend-line mp-compact">
          <div className="mp-label mp-legend-corner" style={{ width: labelW }}>
            Leg.
          </div>
          <div className="mp-legend-panel">
            <PlanningLegend
              clipboard={null}
              canEdit={false}
              canViewPendingAbsences={false}
            />
          </div>
        </div>
        <div className="mp-line mp-calendar-head mp-compact">
          <PlanningHeadRow
            labelW={labelW}
            totalWidth={totalWidth}
            dayW={dayW}
            months={months}
            weekGroups={weekGroups}
            days={days}
            calendarByDay={calendarByDay}
            today={today}
            isCompact
            headScrollLeft={headScrollLeft}
            headTrackVisibleWidth={headTrackVisibleWidth}
            trackScrollRef={headTrackScrollRef}
          />
        </div>
      </div>
      <div
        ref={scrollRef}
        className="mp-display-scroll mp-scroll mp-compact mp-display-scroll--kiosk"
        onPointerDown={onPanPointerDown}
      >
        <div className="mp-grid" style={{ minHeight: gridHeight }}>
          {rows.map((row) => {
            if (row.kind === "separator") {
              const separatorLabel = row.label?.trim();
              const sepIdx = separatorIndex++;
              const sepHeight = separatorHeights[sepIdx] ?? MP_DISPLAY_SEPARATOR_H;
              return (
                <div key={row.id} className="mp-line mp-row-separator">
                  <div
                    className="mp-label mp-separator-label"
                    style={{ width: labelW, minHeight: sepHeight, height: sepHeight }}
                  >
                    {separatorLabel ? (
                      <span className="mp-separator-title">{separatorLabel}</span>
                    ) : null}
                  </div>
                  <div
                    className="mp-track mp-separator-track"
                    style={{ width: totalWidth, minHeight: sepHeight, height: sepHeight }}
                    aria-hidden
                  >
                    <div className="mp-separator-line" />
                  </div>
                </div>
              );
            }

            const trackLayout = rowLayouts.get(row.id)!;
            const trackHeight = rowHeights.get(row.id) ?? rowHeightForLanes(
              trackLayout.laneCount,
              metrics.barH,
              metrics.barGap,
              metrics.rowPadTop,
            );
            const barTopForLane = (lane: number) =>
              barTopForTrack(
                lane,
                trackLayout.laneCount,
                trackHeight,
                metrics.barH,
                metrics.barGap,
              );
            return (
              <div
                key={row.id}
                className={clsx("mp-line", row.kind === "resource" && "mp-row-resource")}
              >
                <div
                  className="mp-label"
                  style={{
                    width: labelW,
                    height: trackHeight,
                    minHeight: trackHeight,
                    maxHeight: trackHeight,
                  }}
                  title={rowLabelFull(row)}
                >
                  {row.color ? (
                    <span
                      className="inline-block shrink-0 rounded-full"
                      style={{
                        backgroundColor: row.color,
                        width: scaleDisplayPx(10, scaledUi),
                        height: scaleDisplayPx(10, scaledUi),
                      }}
                    />
                  ) : null}
                  <span className="mp-label-text mp-label-abbrev truncate">{rowLabelFull(row)}</span>
                </div>
                <div
                  className="mp-track"
                  style={{ width: totalWidth, height: trackHeight, minHeight: trackHeight }}
                >
                  {todayIdx >= 0 ? (
                    <div
                      className="mp-today-marker"
                      style={{ left: todayIdx * dayW, width: dayW }}
                      aria-hidden
                    />
                  ) : null}
                  <div className="mp-track-bg">
                    {days.map((d) => {
                      const iso = toIso(d);
                      const calendarDay = calendarByDay.get(iso);
                      const officeUnavail =
                        row.kind === "employee" &&
                        isUnavailableDay(row.id, iso, unavailabilities);
                      const unavailBlock = officeUnavail
                        ? findUnavailabilityOnDay(row.id, iso, unavailabilities)
                        : undefined;
                      const nonWorkWeekend =
                        row.kind === "employee"
                          ? isNonWorkDay(d, row.non_work_weekdays)
                          : isWeekend(d);
                      const isTodayCell = isSameDay(d, today);
                      return (
                        <div
                          key={iso}
                          title={
                            calendarDay?.label ??
                            (unavailBlock
                              ? unavailabilityDayTitle(unavailBlock)
                              : nonWorkWeekend || officeUnavail
                                ? nonWorkDayTitle(d, row.non_work_weekdays)
                                : undefined)
                          }
                          className={clsx(
                            "mp-daycell",
                            officeUnavail && "mp-unavailable",
                            nonWorkWeekend && !officeUnavail && "mp-weekend",
                            !officeUnavail && calendarDay?.kind === "holiday" && "mp-holiday",
                            !officeUnavail && calendarDay?.kind === "bridge" && "mp-bridge",
                            !officeUnavail &&
                              calendarDay?.kind === "betriebsferien" &&
                              "mp-betriebsferien",
                            !officeUnavail && isTodayCell && "mp-today",
                          )}
                          style={{ width: dayW }}
                        />
                      );
                    })}
                  </div>

                  {row.kind === "employee" ? (
                    <UnavailabilityReasonLabels
                      rowId={row.id}
                      blocks={unavailabilities}
                      dayIsos={days.map((d) => toIso(d))}
                      dayWidth={dayW}
                    />
                  ) : null}

                  {[...trackLayout.absences.values()].map(({ absence, geom, lane }) => (
                    <AbsenceBar
                      key={`abs-${absenceLayoutKey(absence)}`}
                      absence={absence}
                      geom={geom}
                      dayWidth={dayW}
                      barHeight={metrics.barH}
                      barTop={barTopForLane(lane)}
                    />
                  ))}

                  {row.system_key === "meros" ? (
                    <MerosStateBars
                      days={days}
                      dayWidth={dayW}
                      barHeight={metrics.barH}
                      barTop={metrics.rowPadTop}
                    />
                  ) : null}

                  {(assignmentsByRow.get(row.id) ?? []).map((assignment) => {
                    const laneLayout = trackLayout.assignments.get(assignment.id);
                    if (!laneLayout) return null;
                    const { geom, lane } = laneLayout;
                    return (
                      <AssignmentBar
                        key={assignment.id}
                        assignment={assignment}
                        row={row}
                        geom={geom}
                        canEdit={false}
                        dayWidth={dayW}
                        barHeight={metrics.barH}
                        barTop={barTopForLane(lane)}
                        isDragging={false}
                        onEdit={() => {}}
                        onDragStart={() => {}}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function computeDisplayDateRange(
  today: Date,
  visibleDays: number,
): { from: Date; to: Date } {
  const side = Math.max(visibleDays, 28);
  return {
    from: addDays(today, -(MP_DISPLAY_TODAY_OFFSET + side)),
    to: addDays(today, visibleDays - MP_DISPLAY_TODAY_OFFSET + side),
  };
}
